import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { ExternalLink, MoreVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/utils';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import type { LeadItem, LeadsResponse, Campaign } from '@/types/api';
import { LeadFilters } from './LeadFilters';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { useLeadsStream } from './lib/useLeadsStream';

export interface LeadsQuery {
  campaign_id?: string[];
  state?: string[];
  has_email?: string;
  has_phone?: string;
  updated_days?: string;
}

const TERMINAL_STATES = new Set([
  'meeting_booked',
  'not_interested',
  'nrp_max',
  'opt_out',
  'skip_disqualified',
]);

export function getStateTone(state: string): BadgeTone {
  if (state.startsWith('qualified_via') || state === 'meeting_booked') return 'green';
  if (state === 'not_interested' || state === 'nrp_max' || state === 'opt_out') return 'red';
  if (state.includes('running') || state === 'call_queued' || state === 'bigcall_pushed') return 'yellow';
  if (state === 'skip_disqualified') return 'gray';
  if (state.includes('replied') || state === 'call_in_progress') return 'purple';
  if (TERMINAL_STATES.has(state)) return 'gray';
  return 'blue';
}

export default function LeadsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const [filters, setFilters] = useState<LeadsQuery>({});
  const [selectedLead, setSelectedLead] = useState<LeadItem | null>(null);
  const [page, setPage] = useState(0);

  const qc = useQueryClient();
  const LIMIT = 50;

  // Build query params
  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (workspaceId) p.workspace_id = workspaceId;
    if (filters.has_email) p.has_email = filters.has_email;
    if (filters.has_phone) p.has_phone = filters.has_phone;
    if (filters.updated_days) {
      const since = new Date(Date.now() - parseInt(filters.updated_days, 10) * 86400_000);
      p.updated_since = since.toISOString();
    }
    p.limit = String(LIMIT);
    p.offset = String(page * LIMIT);
    return p;
  }, [workspaceId, filters, page]);

  // Multi-value params (state[], campaign_id[]) need special handling
  const queryKey = ['leads', workspaceId, filters, page];

  const { data, isLoading } = useQuery<LeadsResponse>({
    queryKey,
    queryFn: async () => {
      const url = new URL('/v1/leads', window.location.origin);
      for (const [k, v] of Object.entries(queryParams)) url.searchParams.set(k, v);
      (filters.state ?? []).forEach((s) => url.searchParams.append('state[]', s));
      (filters.campaign_id ?? []).forEach((c) => url.searchParams.append('campaign_id', c));
      const res = await api.get<LeadsResponse>(url.pathname + url.search);
      return res;
    },
    enabled: !!workspaceId,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  // SSE updates
  const streamUpdates = useLeadsStream(workspaceId, filters.campaign_id?.[0]);

  // Apply SSE updates to cached data
  useMemo(() => {
    if (!streamUpdates.length) return;
    const latest = streamUpdates[streamUpdates.length - 1]!;
    qc.setQueryData<LeadsResponse>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.map((item) =>
          item.state_id === latest.state_id
            ? { ...item, state: latest.new_state, updated_at: latest.timestamp }
            : item,
        ),
      };
    });
  // streamUpdates is the only dependency we need — queryKey changes would cause loops
  }, [streamUpdates]);  

  // Campaigns for filter sidebar (derive from loaded leads)
  const campaigns = useMemo<Campaign[]>(() => {
    const seen = new Map<string, string>();
    (data?.items ?? []).forEach((item) => {
      if (item.campaign_id && item.campaign_name) {
        seen.set(item.campaign_id, item.campaign_name);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data?.items]);

  const columns = useMemo<ColumnDef<LeadItem>[]>(
    () => [
      {
        id: 'contact',
        header: 'Contact',
        cell: ({ row }) => {
          const { email, first_name, last_name, company_name, hubspot_contact_id } = row.original;
          return (
            <div>
              <p className="font-medium text-sm">
                {[first_name, last_name].filter(Boolean).join(' ') || email || '—'}
              </p>
              <p className="text-xs text-gray-500">{company_name ?? '—'}</p>
              {hubspot_contact_id && (
                <a
                  href={`https://app.hubspot.com/contacts/0/contact/${hubspot_contact_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-xs text-brand-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  HubSpot <ExternalLink size={9} />
                </a>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'state',
        header: 'État',
        cell: ({ getValue }) => {
          const s = getValue<string>();
          return <Badge tone={getStateTone(s)}>{s}</Badge>;
        },
      },
      {
        accessorKey: 'channels_resolved',
        header: 'Canaux',
        enableSorting: false,
        cell: ({ getValue }) => {
          const channels = (getValue<string[] | null>() ?? []);
          return (
            <div className="flex gap-1 flex-wrap">
              {channels.map((c) => (
                <Badge key={c} tone="blue">{c}</Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: 'provider',
        header: 'Provider',
        enableSorting: false,
        cell: ({ row }) => {
          const { state, sender_email, concurrent_actions } = row.original;
          if (state === 'mail_running' && sender_email) return <span className="text-xs">{sender_email}</span>;
          if (state === 'call_queued' && concurrent_actions?.length)
            return <span className="text-xs text-gray-500">{concurrent_actions[0]}</span>;
          return <span className="text-xs text-gray-400">—</span>;
        },
      },
      {
        accessorKey: 'updated_at',
        header: 'Mis à jour',
        cell: ({ getValue }) => (
          <span className="text-xs text-gray-500">{formatRelative(getValue<string>())}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <ActionsMenu lead={row.original} onDetail={() => setSelectedLead(row.original)} />
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader title="Leads" description="Tous les leads en cours de dispatch" />

      <div className="flex gap-6">
        <LeadFilters filters={filters} onChange={(f) => { setFilters(f); setPage(0); }} campaigns={campaigns} />

        <div className="flex-1 min-w-0">
          <DataTable
            data={data?.items}
            columns={columns}
            loading={isLoading}
            searchPlaceholder="Filtrer par email, société…"
            onRowClick={setSelectedLead}
            emptyTitle="Aucun lead"
            emptyDescription="Les leads apparaîtront ici dès que le dispatch démarre."
            rightSlot={
              data && (
                <span className="text-xs text-gray-400">
                  {data.total} lead{data.total > 1 ? 's' : ''}
                  {data.page_info.has_more && (
                    <button
                      type="button"
                      className="ml-2 text-brand-primary hover:underline"
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Suivant →
                    </button>
                  )}
                  {page > 0 && (
                    <button
                      type="button"
                      className="ml-2 text-brand-primary hover:underline"
                      onClick={() => setPage((p) => p - 1)}
                    >
                      ← Précédent
                    </button>
                  )}
                </span>
              )
            }
          />
        </div>
      </div>

      <LeadDetailDrawer lead={selectedLead} onClose={() => setSelectedLead(null)} />
    </div>
  );
}

function ActionsMenu({ lead, onDetail }: { lead: LeadItem; onDetail: () => void }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  async function handleQualify(intent: string) {
    setOpen(false);
    try {
      await api.post(`/v1/leads/${lead.state_id}/qualify-manual`, { intent });
      toast.success('Qualifié');
      qc.invalidateQueries({ queryKey: ['leads'] });
    } catch {
      toast.error('Échec');
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="p-1 rounded hover:bg-gray-100 text-gray-400"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        aria-label="Actions"
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <ul className="absolute right-0 top-6 z-20 card w-44 py-1 shadow-lg text-xs">
            <li>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50" onClick={(e) => { e.stopPropagation(); setOpen(false); onDetail(); }}>
                Voir détail
              </button>
            </li>
            <li>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50" onClick={(e) => { e.stopPropagation(); handleQualify('positive'); }}>
                Qualifier manuel
              </button>
            </li>
            <li>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50" onClick={(e) => { e.stopPropagation(); handleQualify('meeting_booked'); }}>
                Push à BIG.call (force)
              </button>
            </li>
            <li>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-50 text-red-600" onClick={(e) => { e.stopPropagation(); handleQualify('negative'); }}>
                Pas intéressé
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
